import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FileText, FileDown, Brain, Layers, Search, UploadCloud } from "lucide-react";

const KINDS = {
  note: { label: "Plan note", icon: FileText, cls: "bg-teal-50 text-teal-700" },
  pdf: { label: "Full PDF", icon: FileDown, cls: "bg-sky-50 text-sky-700" },
  phase_pdf: { label: "Phase PDF", icon: Layers, cls: "bg-amber-50 text-amber-700" },
  brain: { label: "Clinical Brain", icon: Brain, cls: "bg-indigo-50 text-indigo-700" },
};
const when = (iso) => new Date(iso).toLocaleString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });

const SendRow = ({ s, showPatient }) => {
  const k = KINDS[s.kind] || KINDS.note;
  const Icon = k.icon;
  return (
    <li className="flex flex-wrap items-center gap-2 border-b border-slate-100 py-2 text-sm last:border-0" data-testid={`send-${s.id}`}>
      <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold ${k.cls}`}><Icon className="h-3 w-3" /> {k.label}</span>
      {showPatient && <span className="font-medium text-slate-800">{s.patient_name}</span>}
      <span className="text-slate-600">{s.label}</span>
      {s.plan_deleted && <span className="text-[10px] italic text-slate-400">(plan since deleted)</span>}
      <span className="ml-auto text-[11px] text-slate-400">{when(s.ts)} · {s.by}</span>
    </li>
  );
};

export const PlanSends = ({ planId }) => {
  const [items, setItems] = useState([]);
  useEffect(() => {
    if (!planId) { return; }
    api.get("/cliniko-sends", { params: { plan_id: planId } }).then((r) => setItems(r.data || [])).catch(() => setItems([]));
  }, [planId]);
  return (
    <div className="no-print mb-3 rounded-xl border border-slate-200 bg-slate-50 p-3" data-testid="plan-sends">
      <div className="mb-1 flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest text-slate-500"><UploadCloud className="h-3.5 w-3.5" /> Sent to Cliniko</div>
      {items.length === 0 ? <p className="text-xs text-slate-400" data-testid="plan-sends-empty">Nothing from this plan has been sent to Cliniko yet.</p>
        : <ul>{items.map((s) => <SendRow key={s.id} s={s} />)}</ul>}
    </div>
  );
};

export const ClinikoSendsList = () => {
  const [items, setItems] = useState(null);
  const [q, setQ] = useState("");
  const [kind, setKind] = useState("all");
  useEffect(() => { api.get("/cliniko-sends").then((r) => setItems(r.data || [])).catch(() => setItems([])); }, []);
  const groups = useMemo(() => {
    const g = {};
    (items || []).filter((s) => (kind === "all" || s.kind === kind) && (!q || (s.patient_name || "").toLowerCase().includes(q.toLowerCase())))
      .forEach((s) => { (g[s.cliniko_id] = g[s.cliniko_id] || { name: s.patient_name, list: [] }).list.push(s); });
    return Object.entries(g);
  }, [items, q, kind]);

  return (
    <div data-testid="cliniko-sends">
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="relative w-64"><Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" /><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search patient..." className="bg-white pl-8" data-testid="sends-search" /></div>
        <Select value={kind} onValueChange={setKind}>
          <SelectTrigger className="w-44 bg-white" data-testid="sends-filter-kind"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="all">Everything</SelectItem>
            {Object.entries(KINDS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      {items && groups.length === 0 && <div className="grid h-48 place-items-center rounded-2xl border border-dashed border-slate-300 text-sm text-slate-400" data-testid="sends-empty">Nothing sent to Cliniko matches these filters</div>}
      <div className="space-y-3">
        {groups.map(([cid, g]) => (
          <div key={cid} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm" data-testid={`sends-patient-${cid}`}>
            <div className="mb-1 flex items-baseline justify-between">
              <div className="font-heading font-bold text-slate-900">{g.name}</div>
              <span className="text-[11px] text-slate-400">{g.list.length} item{g.list.length === 1 ? "" : "s"}</span>
            </div>
            <ul>{g.list.map((s) => <SendRow key={s.id} s={s} />)}</ul>
          </div>
        ))}
      </div>
    </div>
  );
};
